import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ApiError, CompanyData, DataWithUpload, Draft, useCompanyMutation, useStore } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { CompanyEditForm, CompanyHeader, LayoutHeader, NavigationDelete } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const CompanyEdit = () => {
  const [confirmDelete, setConfirmDelete] = useState<Draft<CompanyData>>()
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const { message } = useStore()
  const [{ companyId }] = useNumberParams()
  const mutation = useCompanyMutation()

  const { t } = useTranslation()

  const save = async (draft: Draft<DataWithUpload<CompanyData>>) => {
    try {
      setErrors({})
      await mutation.update(draft)
      message.add({
        message: t('Company was updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`COMPANY: Company failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Company could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }

    router.push(`/companies/${companyId}/detail`)
  }

  const remove = async () => {
    try {
      await mutation.delete(confirmDelete!)
      message.add({
        message: t('Company was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as ApiError
      console.debug(`COMPANY: Company deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Company could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    router.push(`/companies`)
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <CompanyHeader companyId={companyId} breadcrumbItems={[{ title: t('Edit') }]} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<CompanyEditForm.Skeleton />}>
          <CompanyEditForm
            companyId={companyId}
            onSave={save}
            onDelete={async (draft) => setConfirmDelete(draft)}
            errors={errors}
          />
        </Suspense>
        <NavigationDelete
          entity={t('Company')}
          visible={!!confirmDelete}
          onClose={() => setConfirmDelete(undefined)}
          onDelete={remove}
        />
      </View>
    </View>
  )
}

export default CompanyEdit
